import React, { useMemo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Row,
} from "reactstrap";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { getChannelPartnerDashboard } from "action/channelPartner";
import { channelPartnerDashboard } from "constants/channelPartner";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";

const ChannelPartnerDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const dashboardResponse = useSelector(
    (state) => state.channelPartner.channelPartnerDashboard,
  );

  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
  });
  const [pageNum, setPageNum] = useState(1);

  useEffect(() => {
    getChannelPartnerDashboard(dispatch, pageNum);

    return () => {
      dispatch({ type: channelPartnerDashboard, payload: {} });
    };
  }, [dispatch, pageNum]);

  useEffect(() => {
    if (dashboardResponse?.code === 200 || dashboardResponse?.summary) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: (dashboardResponse?.upcomingBatches || []).length === 0,
      });
    } else if (dashboardResponse?.code || dashboardResponse?.message) {
      setListState({
        isLoading: false,
        error:
          dashboardResponse?.message ||
          "Failed to load channel partner dashboard. Please try again.",
        noRecords: false,
      });
    }
  }, [dashboardResponse]);

  const summaryCards = [
    {
      title: "Total Trainees",
      value: dashboardResponse?.summary?.totalTrainees ?? 0,
      subtitle: "Trainees registered through this partner",
      icon: "ni ni-single-02",
      theme: "purple",
      path: "/channel-partner/trainee",
    },
    {
      title: "Recent Registrations",
      value: dashboardResponse?.summary?.recentRegistrations ?? 0,
      subtitle: "Registrations in the last 7 days",
      icon: "ni ni-user-run",
      theme: "green",
      path: "/channel-partner/trainee",
    },
    {
      title: "Course Enrollments",
      value: dashboardResponse?.summary?.enrolledTrainees ?? 0,
      subtitle: "Enrollments across available courses",
      icon: "ni ni-book-bookmark",
      theme: "blue",
      path: "/channel-partner/my-courses",
    },
    {
      title: "Certificates Issued",
      value: dashboardResponse?.summary?.certificatesIssued ?? 0,
      subtitle: "Certificates issued to associated trainees",
      icon: "ni ni-paper-diploma",
      theme: "teal",
      path: "/channel-partner/my-courses",
    },
  ];

  const batchData = useMemo(() => {
    const batches = dashboardResponse?.upcomingBatches || [];

    return batches.map((item, index) => ({
      ...item,
      srNo:
        (pageNum - 1) *
          (dashboardResponse?.upcomingBatchesNoOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
      batchName: item?.batchName || "-",
      courseName: item?.courseName || "-",
      registrationStartDate: item?.registrationStartDate || "-",
      startDate: item?.startDate || "-",
      endDate: item?.endDate || "-",
    }));
  }, [dashboardResponse, pageNum]);

  const batchColumns = useMemo(
    () => [
      {
        Header: "Sr. No.",
        accessor: "srNo",
        minWidth: 60,
        maxWidth: 70,
        centerAlign: true,
      },
      {
        Header: "Batch Name",
        accessor: "batchName",
        minWidth: 200,
        maxWidth: 200,
        Cell: ({ value }) => (
          <span className="text-wrapper">{value || "-"}</span>
        ),
      },
      {
        Header: "Course Name",
        accessor: "courseName",
        minWidth: 220,
        maxWidth: 220,
        Cell: ({ value }) => (
          <span className="text-wrapper">{value || "-"}</span>
        ),
      },
      {
        Header: "Registration Start Date",
        accessor: "registrationStartDate",
        minWidth: 180,
        maxWidth: 200,
        Cell: ({ value }) => (
          <span className="text-wrapper">{value || "-"}</span>
        ),
      },
      {
        Header: "Start Date",
        accessor: "startDate",
        minWidth: 160,
        maxWidth: 180,
        Cell: ({ value }) => (
          <span className="text-wrapper">{value || "-"}</span>
        ),
      },
      {
        Header: "End Date",
        accessor: "endDate",
        minWidth: 160,
        maxWidth: 180,
        Cell: ({ value }) => (
          <span className="text-wrapper">{value || "-"}</span>
        ),
      },
    ],
    [],
  );

  const renderSummaryCard = (card) => (
    <Col xl="3" md="6" sm="6" xs="12" key={card.title} className="mb-4">
      <Card
        className={`dashboard-summary-card dashboard-summary-card--${card.theme}`}
        onClick={() => navigate(card.path)}
        role="link"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            navigate(card.path);
          }
        }}
      >
        <CardBody>
          <div className="dashboard-summary-card__content">
            <p className="dashboard-summary-card__title">{card.title}</p>
            <h2 className="dashboard-summary-card__value">{card.value}</h2>
            <p className="dashboard-summary-card__subtitle">{card.subtitle}</p>
          </div>

          <div className="dashboard-summary-card__watermark" aria-hidden="true">
            <i className={card.icon} />
          </div>
        </CardBody>
      </Card>
    </Col>
  );

  const renderSectionHeader = (title, path, buttonText = "View All") => (
    <CardHeader className="d-flex align-items-center justify-content-between">
      <div>
        <CardTitle tag="h3" className="mb-0 text-default">
          {title}
        </CardTitle>
      </div>

      {path && (
        <Button
          color="link"
          className="dashboard-view-all"
          onClick={() => navigate(path)}
        >
          {buttonText}
          <i className="ni ni-bold-right" />
        </Button>
      )}
    </CardHeader>
  );

  const renderNoRecords = (message = "No records found.") => (
    <div className="loaderContainer empty-style">
      <div className="empty-icon">
        <i className="fa-solid fa-circle-info" />
      </div>
      <span>{message}</span>
    </div>
  );

  return (
    <Container fluid className="admin-dashboard">
      <Row className="dashboard-summary-grid">
        {summaryCards.map(renderSummaryCard)}
      </Row>

      <Row>
        <Col className="mb-4">
          <Card>
            {renderSectionHeader(
              "Upcoming Batches",
              "/channel-partner/my-courses",
            )}

            <CardBody>
              {listState.isLoading ? (
                <div className="loaderContainer">
                  <i className="fa fa-spinner fa-spin fa-2xl loader" />
                </div>
              ) : listState.error ? (
                <div className="loaderContainer error-style">
                  <div className="error-icon">
                    <i className="fa-solid fa-circle-exclamation" />
                  </div>
                  <span>{listState.error}</span>
                </div>
              ) : listState.noRecords ? (
                renderNoRecords("No upcoming batches found.")
              ) : (
                <div className="table-responsive">
                  <CommonTable
                    columns={batchColumns}
                    data={batchData || []}
                    defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                    activePage={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      (dashboardResponse?.upcomingBatchesTotalRecords || 0) /
                        (dashboardResponse?.upcomingBatchesNoOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                    )}
                    showPaginationBottom={true}
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ChannelPartnerDashboard;
